import React from "react";
import { FormikProps } from "formik";
import { FormGroup } from "reactstrap";

import { EditDeviceFormFields } from "../../../../../api/graphql/workplaces";
import {
  DEVICE_LOCALE_OPTIONS,
  DEVICE_TIME_ZONE_OPTIONS,
} from "../../../../Devices/lib/deviceDateTime";

import { Input } from "../../../Input/Input";
import { LabelWithInformationBox } from "../../../LabelWithInformationBox/LabelWithInformationBox";
import { InputDescription } from "../../../InputDescription/InputDescription";
import { Switch } from "../../../Switch/Switch";
import { SectionHeader } from "../ui/SectionHeader";

export type DateTimeFormFields = Pick<
  EditDeviceFormFields,
  "timeZone" | "locale" | "autoTime" | "use24HourTime"
>;

interface Props {
  // Only what the section reads, so both the Device Modal's form and the
  // bulk wizard's smaller one fit
  formik: Pick<
    FormikProps<DateTimeFormFields>,
    | "values"
    | "errors"
    | "touched"
    | "handleChange"
    | "setFieldValue"
    | "setFieldTouched"
  >;
  // The modal doesn't link to the bulk wizard yet, so the link is hidden without it
  multiDeviceHref?: string;
}

export const DateTimeSection = ({ formik, multiDeviceHref }: Props) => {
  const handleSwitchChange = (
    name: "autoTime" | "use24HourTime",
    value: boolean
  ) => {
    formik.setFieldValue(name, value);
    formik.setFieldTouched(name);
  };

  return (
    <section className="DeviceSection DateTimeSection">
      <SectionHeader icon="clock-icon" title="Date and time" />

      <FormGroup>
        <LabelWithInformationBox for="timeZone" title="Time zone" />
        <Input
          type="select"
          id="timeZone"
          name="timeZone"
          value={formik.values.timeZone ?? ""}
          onChange={formik.handleChange}
          isTouched={formik.touched.timeZone}
          errorMessage={formik.errors.timeZone}
          invalid={!!formik.errors.timeZone && formik.touched.timeZone}
        >
          <option value="" disabled>
            Select time zone
          </option>
          {DEVICE_TIME_ZONE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      </FormGroup>
      <FormGroup>
        <LabelWithInformationBox for="locale" title="Date format (locale)" />
        <Input
          type="select"
          id="locale"
          name="locale"
          value={formik.values.locale ?? ""}
          onChange={formik.handleChange}
          isTouched={formik.touched.locale}
          errorMessage={formik.errors.locale}
          invalid={!!formik.errors.locale && formik.touched.locale}
        >
          <option value="" disabled>
            Select date format
          </option>
          {DEVICE_LOCALE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
        <InputDescription description="Applies country-specific date formats and translations." />
      </FormGroup>

      <Switch
        name="autoTime"
        title="Set time automatically"
        value={formik.values.autoTime}
        onChange={(value) => handleSwitchChange("autoTime", value)}
      />
      <Switch
        name="use24HourTime"
        title="24-hour time"
        value={formik.values.use24HourTime}
        onChange={(value) => handleSwitchChange("use24HourTime", value)}
      />

      {multiDeviceHref && (
        <a
          href={multiDeviceHref}
          target="_blank"
          rel="noopener noreferrer"
          className="DateTimeSection__link"
        >
          Edit date and time for multiple devices
          <svg
            aria-hidden="true"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
          >
            <path
              d="M3.5 8.5l5-5M4.5 3.5h4v4"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </a>
      )}
    </section>
  );
};
